import { useEffect, useState } from "react";

import { Button, Line, ModalBody, ModalHeader, P, toast } from "@sorcery/ui";

import { useModal } from "~/providers/ModalProvider";

import { api } from "~/utils/trpc";

type Props = {
  deckId: string;
  deckName: string;
};

export const MoveDeckToFolderModal = ({ deckId, deckName }: Props) => {
  const { closeModal } = useModal();
  const [error, setError] = useState("");
  const [selectedFolderId, setSelectedFolderId] = useState("");
  const trpcUtils = api.useUtils();

  const { data: folders } = api.deck.getMyFolders.useQuery();
  const { data: deck } = api.deck.getById.useQuery({ id: deckId });
  const { data: folder } = api.deck.getFolderById.useQuery(
    // biome-ignore lint/style/noNonNullAssertion: <explanation>
    { id: deck?.folderId! },
    {
      enabled: !!deck?.folderId,
    },
  );

  const { mutateAsync: moveDeck, error: trpcError } = api.deck.moveDeckToFolder.useMutation({
    onSuccess: () => {
      toast("Deck moved");
      void trpcUtils.deck.getMyFolders.invalidate();
      void trpcUtils.deck.getByUserId.invalidate();
      closeModal();
    },
  });

  const validMoveTargets = folders?.filter((f) => f.id !== deck?.folderId) ?? [];

  // Track trpc errors for display
  useEffect(() => {
    if (trpcError) {
      // don't expose server side error messages
      setError("Something went wrong.");
    }
  }, [trpcError]);

  const onSubmit = () => {
    if (!selectedFolderId) {
      setError("Please select a folder");
      return;
    }
    // clear errs
    setError("");
    void moveDeck({ deckId, folderId: selectedFolderId });
  };

  return (
    <div className="p-6  max-h-[500px] overflow-y-scroll">
      {/* header */}
      <ModalHeader title="Move Deck to Folder" />

      <ModalBody>
        {/* item */}
        <div className="mb-2 w-full">
          {folder && (
            <P className="mb-4">
              <span>Deck is currently in "{folder.name}"</span>
            </P>
          )}

          {!!validMoveTargets.length && (
            <>
              <P className="mb-4">
                <span>Select a folder to move "{deckName}" to:</span>
              </P>

              <div className="space-y-2">
                {validMoveTargets.map((folder) => (
                  <label
                    key={folder.id}
                    className="flex items-center space-x-2 p-2 hover:bg-gray-100 hover:text-gray-900 rounded cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="folder"
                      checked={selectedFolderId === folder.id}
                      onChange={() => setSelectedFolderId(folder.id)}
                      className="h-4 w-4 "
                    />
                    <span>{folder.name}</span>
                  </label>
                ))}
              </div>
            </>
          )}

          {error && (
            <div className="text-red my-4" role="alert">
              <p>
                <span className="font-bold">Error. </span>
                {error}
                <span>.</span>
              </p>
            </div>
          )}
        </div>
      </ModalBody>

      {/* footer */}
      <Line />
      <div className="flex flex-row-reverse pt-4 gap-2">
        <Button onClick={onSubmit}>Move Deck</Button>
        {folder && (
          <Button onClick={() => void moveDeck({ deckId, folderId: null })} variant="secondary">
            Remove from Folder
          </Button>
        )}
        <Button onClick={closeModal} variant="secondary">
          Cancel
        </Button>
      </div>
    </div>
  );
};
